/** Combinations are authored English-first: `meta.nameEN` is the master field
 *  the library detail editor writes to, and the only one guaranteed to be
 *  filled, so it is the fallback when the UI language has no curated name. */
const FALLBACK_LANG = "EN";

/** The remaining locales, tried only after the UI language and EN both came up
 *  empty - better a name in the wrong language than an untitled tile. */
const LAST_RESORT_LANGS = ["DE", "FR", "IT"];

/** Trimmed value, or undefined when the field is missing, blank or not a
 *  string - a whitespace-only name is as absent as no field at all. */
function validName(value: unknown): string | undefined {
	if (typeof value !== "string") return undefined;
	const trimmed = value.trim();
	return trimmed === "" ? undefined : trimmed;
}

/**
 * The name of a combination as it should be shown to the user, in descending
 * priority:
 *
 *   1. the curated name for the current UI language (`meta.nameDE` / `EN` /
 *      `FR` / `IT`),
 *   2. `meta.nameEN`, the authoring locale,
 *   3. any other curated name that happens to be filled.
 *
 * Kept in one place so a combination reads the same wherever it is shown - the
 * main library gallery, the scene library gallery and the anonymous
 * configurator each used to resolve this inline, and the main library one
 * pinned every tile to English regardless of the user's language setting.
 *
 * @param language full i18next language tag, e.g. "de-CH" - the region is
 *                 dropped, only "DE"/"EN"/"FR"/"IT" exist on the record.
 */
export function getCombinationDisplayName(
	combination: { meta?: Record<string, any> } | null | undefined,
	language: string,
): string | undefined {
	const meta = combination?.meta;
	if (!meta) return undefined;
	const lang = language.split("-")[0].toUpperCase();
	return (
		validName(meta[`name${lang}`]) ??
		validName(meta[`name${FALLBACK_LANG}`]) ??
		LAST_RESORT_LANGS.map((code) => validName(meta[`name${code}`])).find(
			(name) => name !== undefined,
		)
	);
}
